
import { jsPDF } from "jspdf";
import { GalleryPhoto } from "@/components/gallery/carousel/types";
import { PdfExportOptions } from "../types";
import { 
  addImagePlaceholder, 
  calculateLayoutConfig, 
  addPageNumbers,
  createSafeFileName,
  addPdfTitle,
  addDecorativeLine
} from "../utils/pdfUtils";
import { prepareImageForPdf } from "../utils/imageUtils";

/**
 * Generates a standard gallery PDF (non-pregnancy)
 */
export const generateStandardGalleryPdf = async (
  photos: GalleryPhoto[],
  collectionTitle: string | undefined,
  options: PdfExportOptions
): Promise<string> => {
  // Create a new PDF document
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  // Calculate layout dimensions
  const layout = calculateLayoutConfig(doc);
  
  // Add title
  const title = collectionTitle || 'Photo Collection';
  addPdfTitle(doc, title);
  
  // Add decorative line
  if (options.includeDecorations) {
    addDecorativeLine(doc);
  }
  
  // Start position for photos
  let yPosition = 40;
  
  // Process photos normally
  for (let i = 0; i < photos.length; i++) {
    // Start a new page if needed
    if (yPosition + layout.imageHeight + 10 > doc.internal.pageSize.getHeight()) {
      doc.addPage();
      yPosition = 20;
    }
    
    const photo = photos[i];
    const row = Math.floor(i / layout.imagesPerRow);
    const col = i % layout.imagesPerRow;
    const xPosition = layout.margin + col * layout.imageWidth;
    
    // Process image and add to PDF
    try {
      // Use enhanced image loading
      const imgData = await prepareImageForPdf(photo.url);
      
      if (imgData) {
        // Add image to PDF
        doc.addImage({
          imageData: imgData, 
          format: 'JPEG', 
          x: xPosition, 
          y: yPosition, 
          width: layout.imageWidth - 5, 
          height: layout.imageHeight,
          compression: 'FAST'
        });
        
        // Add photo title with safe encoding
        doc.setFontSize(10);
        doc.setTextColor(44, 62, 80);
        // Use a safe position for the text to avoid overflow
        const titleX = xPosition + (layout.imageWidth - 5) / 2;
        const titleY = yPosition + layout.imageHeight + 5;
        // Limit title length for safety
        const safeTitle = photo.title.substring(0, 30);
        doc.text(safeTitle, titleX, titleY, { align: 'center' });
        
        // Add decorative milestone icon if applicable and requested
        if (options.includeMilestones && photo.category === 'milestone') {
          doc.setTextColor(231, 76, 60);
          doc.setFontSize(8);
          
          // Draw a star symbol for milestone
          if (options.includeDecorations) {
            doc.text('★', xPosition, yPosition - 2);
            doc.setFontSize(7);
            doc.text('Milestone', xPosition + 5, yPosition - 2);
          }
        }
        
        // Add photo date if requested
        if (options.includePhotoDates) {
          // Use the created_at timestamp from the photo metadata
          const createDate = photo.created_at ? new Date(photo.created_at).toLocaleDateString() : new Date().toLocaleDateString();
          doc.setFontSize(7);
          doc.setTextColor(127, 140, 141);
          doc.text(createDate, xPosition, yPosition + layout.imageHeight + 12);
        }
      } else {
        // Use placeholder for failed image
        addImagePlaceholder(doc, xPosition, yPosition, layout.imageWidth - 5, layout.imageHeight, photo.title);
      }
      
      // Move to next row if needed
      if (col === layout.imagesPerRow - 1) {
        yPosition += layout.imageHeight + layout.spacing;
      }
    } catch (error) {
      console.error(`Error adding image ${photo.url} to PDF:`, error);
      // Add placeholder for failed image
      addImagePlaceholder(doc, xPosition, yPosition, layout.imageWidth - 5, layout.imageHeight, photo.title);
      
      // Move to next row if needed
      if (col === layout.imagesPerRow - 1) {
        yPosition += layout.imageHeight + layout.spacing;
      }
    }
  }
  
  // Add page numbers
  addPageNumbers(doc);
  
  // Create safe filename and save PDF
  const sanitizedTitle = createSafeFileName(collectionTitle);
  const fileName = `${sanitizedTitle}_photos.pdf`;
  doc.save(fileName);
  
  return fileName;
};
